// https://developers.ada.cx/reference/embed2-reference#actions

import { useLazyQuery } from '@apollo/react-hooks';
import { LicenseStatusType } from 'Admin/components';
import { useAuth0 } from 'Auth';
import GET_CURRENT_USER_LICENSES_QUERY from './getCurrentUserLicensesQuery.graphql';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { VALID_US_STATES_TYPE } from 'Shared/constants/states';
import { getPageFromLocation } from 'Shared/utils';

type CurrentUserLicenses = {
  currentUser: {
    id: string;
    licenses: Array<{
      state: VALID_US_STATES_TYPE;
      status: LicenseStatusType;
    }>;
  };
};

export { GET_CURRENT_USER_LICENSES_QUERY };

const useAda = () => {
  // Use `isAdaOpen` to track starting state across multiple components
  // There should only ever be one on the page, but navigating from the Quote UI to the Agent Portal
  // will cause the Ada button to re-render and lose its state
  const [isOpen, changeIsOpen] = useState<boolean | undefined>(
    window.adaSettings?.toggleCallback?.isDrawerOpen
  );
  if (window.adaSettings?.toggleCallback) {
    window.adaSettings!.toggleCallback!.handler = changeIsOpen;
  }

  return isOpen;
};

export const useAdaControls = () => {
  const isOpen = useAda();
  const location = useLocation();
  const { user, getToken } = useAuth0();

  const [
    fetchLicenses,
    { data: userData, loading: userDataLoading, called: userDataCalled },
  ] = useLazyQuery<CurrentUserLicenses>(GET_CURRENT_USER_LICENSES_QUERY, {});
  // Note: We need a second state to track if we are fetching licenses because the useLazyQuery hook
  // will not update the loading state if the data is already in the cache
  const [openingChat, changeOpeningChat] = useState<boolean>(false);

  const adaEmbed = window.adaEmbed;
  const adaLoaded = !!adaEmbed;

  useEffect(() => {
    if (!userDataCalled) {
      return;
    }

    if (!userDataLoading && openingChat) {
      const name = [user?.firstName, user?.lastName]
        .filter((it) => it)
        .join(' ');
      const page = getPageFromLocation(location);

      getToken()
        .catch(() => null)
        .then((token) => {
          changeOpeningChat(false);
          adaEmbed!.setSensitiveMetaFields({
            authorization: token,
          });
          adaEmbed!.setMetaFields({
            name,
            email: user?.email,
            agency: user?.partner?.name,
            chatter_type: 'agent',
            page_when_chat_opened: page,
            licenses: userData?.currentUser.licenses
              .filter((license) => license.status === 'valid_license')
              .map((license) => license.state)
              .join(', '),
          });
          adaEmbed!.toggle();
        });
    }
  }, [
    openingChat,
    userDataCalled,
    userDataLoading,
    userData,
    user,
    location,
    adaEmbed,
    changeOpeningChat,
    getToken,
  ]);

  const toggleOpen = () => {
    if (adaLoaded && !userDataLoading) {
      if (!isOpen) {
        // This will trigger the useEffect above to call adaEmbed.toggle() once licenses are fetched
        changeOpeningChat(true);
        fetchLicenses();
      } else {
        adaEmbed.toggle();
      }
    }
  };

  return { loaded: adaLoaded, toggleOpen, isOpen: !!isOpen };
};
